import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Bell } from 'lucide-react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth, database, subscribeToThemeSettings } from '../../services/firebase';
import { ref, onValue } from 'firebase/database';
import toast from 'react-hot-toast';
import { setUser } from '../../store/slices/authSlice';
import { fetchSettings } from '../../store/slices/settingsSlice';
import { setActiveTheme, setThemeSettings } from '../../store/slices/uiSlice';

import Header from './Header';
import Footer from './Footer';
import LoadingSpinner from '../common/LoadingSpinner';
import Cart from '../cart/Cart';
import ThemeDecorator from '../common/ThemeDecorator';
import { normalizeUserRecord } from '../../utils/helpers';
import { applyThemeColors } from '../../store/slices/uiSlice';
import { resolveActiveTheme } from '../../utils/themes';
import {
  subscribeUnreadNotifications,
  fetchRecentNotifications,
  showNotificationToast
} from '../../services/notificationService';

const isPopupQueueAlreadyDone = () => {
  try {
    return Boolean(sessionStorage.getItem('psh-store-popup-opened'));
  } catch {
    return false;
  }
};

const Layout = ({ children }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { loading, theme, activeTheme } = useSelector((state) => state.ui);
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const [unreadNotifications, setUnreadNotifications] = React.useState(0);
  const [popupQueueDone, setPopupQueueDone] = React.useState(() => {
    const popupAlreadyHandled = isPopupQueueAlreadyDone();
    if (popupAlreadyHandled) return true;
    return location.pathname !== '/';
  });
  const lastShownNotificationRef = React.useRef('');
  const previousUnreadRef = React.useRef(0);
  const hasUnreadSnapshotRef = React.useRef(false);
  const shownNotificationIdsRef = React.useRef(new Set());
  const deferredNotificationToastsRef = React.useRef(new Map());
  const popupQueueDoneRef = React.useRef(popupQueueDone);
  const hasShownTelegramPromptRef = React.useRef(false);
  const telegramToastId = 'telegram-not-linked-important';

  const showTelegramNotLinkedToast = React.useCallback(() => {
    toast.custom(
      (toastInstance) => {
        let startX = 0;
        let startY = 0;

        const dismissToast = () => toast.dismiss(toastInstance.id || telegramToastId);
        const openProfile = () => {
          dismissToast();
          navigate('/profile');
        };

        const onTouchStart = (event) => {
          const touch = event.changedTouches?.[0];
          if (!touch) return;
          startX = touch.clientX;
          startY = touch.clientY;
        };

        const onTouchEnd = (event) => {
          const touch = event.changedTouches?.[0];
          if (!touch) return;
          const dx = touch.clientX - startX;
          const dy = touch.clientY - startY;
          if (Math.abs(dx) >= 60 && Math.abs(dy) <= 70) {
            dismissToast();
          }
        };

        return (
          <div
            onClick={openProfile}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
            className={`${
              toastInstance.visible ? 'animate-enter' : 'animate-leave'
            } pointer-events-auto w-[min(92vw,420px)] rounded-xl border border-amber-300 bg-amber-50 text-amber-900 shadow-lg p-3 cursor-pointer`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="text-sm font-semibold">{t('notifications.telegram_not_linked_title', 'Telegram Not Linked')}</div>
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  dismissToast();
                }}
                className="text-amber-700 hover:text-amber-900 text-xs font-semibold"
              >
                {t('notifications.dismiss', 'Dismiss')}
              </button>
            </div>
            <p className="mt-1 text-xs leading-5">
              {t('notifications.telegram_not_linked_message', 'Bind Telegram in Profile to get order updates instantly. Tap this card to open Profile.')}
            </p>
          </div>
        );
      },
      {
        id: telegramToastId,
        duration: Infinity,
        position: 'top-right',
      }
    );
  }, [navigate, t]);

  useEffect(() => {
    // Apply initial theme class
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);


  // Apply festival theme colors
  useEffect(() => {
    applyThemeColors(activeTheme);
  }, [activeTheme]);

  // URL Theme Preview Listener (?theme=... or ?previewTheme=...)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(location.search);
      const preview = urlParams.get('theme') || urlParams.get('previewTheme');
      if (preview) {
        const resolved = resolveActiveTheme(activeTheme, preview);
        if (resolved && resolved !== activeTheme) {
          dispatch(setActiveTheme(resolved));
        }
      }
    }
  }, [location.search, activeTheme, dispatch]);


  useEffect(() => {
    popupQueueDoneRef.current = popupQueueDone;
  }, [popupQueueDone]);

  useEffect(() => {
    const popupAlreadyHandled = isPopupQueueAlreadyDone();
    if (popupAlreadyHandled) {
      popupQueueDoneRef.current = true;
      setPopupQueueDone(true);
      return;
    }

    if (location.pathname !== '/') {
      popupQueueDoneRef.current = true;
      setPopupQueueDone(true);
      return;
    }

    popupQueueDoneRef.current = false;
    setPopupQueueDone(false);
  }, [location.pathname]);

  useEffect(() => {
    if (popupQueueDoneRef.current) return () => {};
    const handlePopupQueueDone = () => {
      popupQueueDoneRef.current = true;
      setPopupQueueDone(true);
    };
    window.addEventListener('psh:popup-queue-done', handlePopupQueueDone);
    return () => window.removeEventListener('psh:popup-queue-done', handlePopupQueueDone);
  }, []);

  useEffect(() => {
    if (!popupQueueDone) return;
    const queued = Array.from(deferredNotificationToastsRef.current.values())
      .filter((item) => item?.id)
      .sort((a, b) => {
        if (a.createdAt !== b.createdAt) return a.createdAt - b.createdAt;
        return String(a.id).localeCompare(String(b.id));
      });

    queued.forEach((item) => {
      if (shownNotificationIdsRef.current.has(item.id)) return;
      shownNotificationIdsRef.current.add(item.id);
      lastShownNotificationRef.current = item.id;
      const type = String(item?.type || '').toLowerCase();
      if (type.includes('telegram_not_linked')) {
        showTelegramNotLinkedToast();
      } else {
        showNotificationToast(item);
      }
    });
    deferredNotificationToastsRef.current.clear();
  }, [popupQueueDone, showTelegramNotLinkedToast]);

  useEffect(() => {
    dispatch(fetchSettings());

    // Subscribe to dynamic theme settings with intelligent scheduling and preview support
    const unsubscribe = subscribeToThemeSettings((settings) => {
      const resolved = resolveActiveTheme(settings?.activeTheme);
      dispatch(setActiveTheme(resolved));
      dispatch(setThemeSettings(settings));
    });

    return () => unsubscribe();
  }, [dispatch]);

  useEffect(() => {
    let unsubscribeStatus = () => { };

    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      // Clean up previous database listener if exists
      unsubscribeStatus();

      if (user) {
        // Setup real-time listener for user profile/status
        const userRef = ref(database, `Users/${user.uid}`);
        
        unsubscribeStatus = onValue(userRef, (snapshot) => {
          if (snapshot.exists()) {
            const rawProfile = snapshot.val();
            const normalized = normalizeUserRecord(rawProfile, {
              email: user.email,
              name: user.displayName || '',
            });

            // REAL-TIME BAN CHECK
            if (normalized.isDisabled) {
              signOut(auth);
              toast.error('This account has been disabled. Access revoked.', { duration: 5000, id: 'ban-toast' });
              return;
            }

            dispatch(setUser({
              uid: user.uid,
              email: normalized.email || user.email,
              displayName: normalized.name || user.displayName,
              photoURL: user.photoURL,
              ...normalized,
            }));
          } else {
            // Fallback if profile doesn't exist yet but user is authed
            dispatch(setUser({
              uid: user.uid,
              email: user.email,
              displayName: user.displayName,
              photoURL: user.photoURL,
            }));
          }
        }, (error) => {
          console.error("Status subscription error:", error);
        });
      } else {
        dispatch(setUser(null));
      }
    });

    return () => {
      unsubscribeAuth();
      unsubscribeStatus();
    };
  }, [dispatch]);

  useEffect(() => {
    if (!user?.uid) {
      setUnreadNotifications(0);
      previousUnreadRef.current = 0;
      lastShownNotificationRef.current = '';
      hasUnreadSnapshotRef.current = false;
      shownNotificationIdsRef.current = new Set();
      deferredNotificationToastsRef.current = new Map();
      hasShownTelegramPromptRef.current = false;
      return () => {};
    }

    let isActive = true;
    const shouldDeferToast = (item) => {
      const type = String(item?.type || '').toLowerCase();
      return type.includes('telegram_not_linked') && !popupQueueDoneRef.current;
    };

    const enqueueToast = (item) => {
      if (!item?.id) return false;
      if (shownNotificationIdsRef.current.has(item.id)) return false;
      if (item.id === lastShownNotificationRef.current) return false;
      if (shouldDeferToast(item)) {
        deferredNotificationToastsRef.current.set(item.id, item);
        return false;
      }
      shownNotificationIdsRef.current.add(item.id);
      lastShownNotificationRef.current = item.id;
      const type = String(item?.type || '').toLowerCase();
      if (type.includes('telegram_not_linked')) {
        showTelegramNotLinkedToast();
      } else {
        showNotificationToast(item);
      }
      return true;
    };

    const showLatestUnreadToast = async () => {
      const recent = await fetchRecentNotifications(user.uid, 20);
      if (!isActive) return 0;

      const unread = recent
        .filter((item) => item?.id && !item.isRead && item.createdAt > 0)
        .sort((a, b) => {
          if (b.createdAt !== a.createdAt) return b.createdAt - a.createdAt;
          return String(b.id).localeCompare(String(a.id));
        });

      const latestUnread = unread[0];
      if (latestUnread?.id) enqueueToast(latestUnread);

      return unread.length;
    };

    const unsubscribe = subscribeUnreadNotifications(user.uid, async (count) => {
      const safeCount = Number.isFinite(Number(count)) ? Number(count) : 0;
      setUnreadNotifications(safeCount);

      if (!hasUnreadSnapshotRef.current) {
        hasUnreadSnapshotRef.current = true;
        try {
          const actualUnreadCount = await showLatestUnreadToast();
          if (!isActive) return;

          const effectiveCount = Math.max(safeCount, actualUnreadCount);
          previousUnreadRef.current = effectiveCount;
          setUnreadNotifications(effectiveCount);
        } catch (error) {
          console.error('Failed to fetch initial unread notifications for toast:', error);
          previousUnreadRef.current = safeCount;
        }
        return;
      }

      if (safeCount <= previousUnreadRef.current) {
        previousUnreadRef.current = safeCount;
        return;
      }

      try {
        const recent = await fetchRecentNotifications(user.uid, 12);
        const unread = recent
          .filter((item) => !item.isRead && item.createdAt > 0)
          .sort((a, b) => {
            if (a.createdAt !== b.createdAt) return a.createdAt - b.createdAt;
            return String(a.id).localeCompare(String(b.id));
          });

        unread.forEach((item) => {
          enqueueToast(item);
        });
      } catch (error) {
        console.error('Failed to fetch latest notifications for toast:', error);
      } finally {
        previousUnreadRef.current = safeCount;
      }
    });

    return () => {
      isActive = false;
      unsubscribe();
    };
  }, [user?.uid, showTelegramNotLinkedToast]);

  useEffect(() => {
    const uid = String(user?.uid || '').trim();
    if (!uid) return;

    const chatId = String(user?.telegramChatId || '').trim();
    const isLinked = Boolean(chatId);

    if (isLinked) {
      hasShownTelegramPromptRef.current = false;
      toast.dismiss(telegramToastId);
      return;
    }

    if (!popupQueueDone) return;
    if (hasShownTelegramPromptRef.current) return;

    hasShownTelegramPromptRef.current = true;
    showTelegramNotLinkedToast();
  }, [user?.uid, user?.telegramChatId, popupQueueDone, showTelegramNotLinkedToast]);

  return (
    <div className="relative isolate flex min-h-screen flex-col bg-gray-50 dark:bg-gray-950">
      <ThemeDecorator />
      <Header unreadNotifications={unreadNotifications} />
      <main className="flex-1 relative">
        {loading && <LoadingSpinner />}
        <div className="min-h-full">
          {children}
        </div>
      </main>
      <Footer />
      <Cart />
      {isAuthenticated && unreadNotifications > 0 && location.pathname !== '/notifications' && (
        <button
          type="button"
          onClick={() => navigate('/notifications')}
          aria-label={t('notifications.open_unread', { count: unreadNotifications }, `Open ${unreadNotifications} unread notifications`)}
          className="fixed bottom-5 right-5 z-[70] group"
        >
          <span className="absolute inset-0 rounded-2xl bg-primary-500/30 animate-ping" />
          <span className="relative flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-600 text-white shadow-2xl shadow-primary-500/25 ring-4 ring-white/80 transition-transform group-hover:scale-105 group-active:scale-95 dark:bg-white dark:text-gray-950 dark:ring-gray-900/80">
            <Bell className="h-6 w-6" />
            <span className="absolute -top-1.5 -right-1.5 min-w-6 h-6 px-1.5 rounded-full bg-red-500 text-white text-[11px] font-black flex items-center justify-center border-2 border-white dark:border-gray-900">
              {unreadNotifications > 99 ? '99+' : unreadNotifications}
            </span>
          </span>
        </button>
      )}
    </div>
  );
};

export default Layout;
